// Minimal snap sections initializer
// Adds IntersectionObserver to toggle .is-active on .snap-section and updates pager dots.
// Exports default init function to be called from MobileLandingPage.

export default function initSnapSections(rootSelector = '#snap-root') {
  if (typeof window === 'undefined') return;
  const root = document.querySelector(rootSelector);
  if (!root) return;

  // Allow forcing snap behavior via data-force-snap attribute or when viewport is narrow.
  const isMobileViewport = window.matchMedia('(max-width: 480px)').matches;
  const forceSnap = root.dataset.forceSnap === 'true';
  if (!isMobileViewport && !forceSnap) return; // preserve original gating unless forced

  const sections = Array.from(root.querySelectorAll('.snap-section'));
  if (!sections.length) return;

  // Ensure the first section is active initially
  sections.forEach((s, i) => s.classList.toggle('is-active', i === 0));

  const dots = Array.from(root.querySelectorAll('.snap-dot'));
  function setActive(index) {
    sections.forEach((s, i) => {
      const activate = i === index;
      s.classList.toggle('is-active', activate);
      // Stabilize re-entry: once a section has animated in the first time,
      // add .has-entered so future activations don't re-apply the initial
      // translateY offset (prevents positional jump when revisiting).
      if (activate && !s.classList.contains('has-entered')) {
        s.classList.add('has-entered');
      }
    });
    dots.forEach((d, i) => d.classList.toggle('dot--active', i === index));
    const prev = root.querySelector('.snap-prev');
    const next = root.querySelector('.snap-next');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === sections.length - 1;
  }

  // Custom scroll animation (shorter & smoother than default 'smooth')
  // Uses requestAnimationFrame with a 240ms duration and an ease-out curve.
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Faster scroll animation for more responsive feel
  const SCROLL_DURATION = reduceMotion ? 0 : 180; // ms
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  let scrollAnimating = false;
  function animateScrollTo(targetY) {
    if (SCROLL_DURATION === 0) {
      root.scrollTo(0, targetY);
      return;
    }
    const startY = root.scrollTop;
    const delta = targetY - startY;
    const startTime = performance.now();
    scrollAnimating = true;
    function frame(now) {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / SCROLL_DURATION);
      const eased = easeOutCubic(progress);
      root.scrollTop = startY + delta * eased;
      if (progress < 1 && scrollAnimating) {
        requestAnimationFrame(frame);
      } else {
        scrollAnimating = false;
      }
    }
    requestAnimationFrame(frame);
  }

  // IntersectionObserver to detect active section
  // Lower threshold so a section becomes active sooner while scrolling
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.intersectionRatio >= 0.4) {
        const idx = sections.indexOf(entry.target);
        if (idx >= 0) setActive(idx);
        // update hash without scrolling (use window.history to avoid ESLint no-restricted-globals)
        if (typeof window !== 'undefined' && window.history && typeof window.history.replaceState === 'function') {
          window.history.replaceState(null, '', `#${entry.target.dataset.section}`);
        }
      }
    });
  }, { root: root, threshold: 0.4 });

  sections.forEach(s => observer.observe(s));

  // Idle snap: when user stops scrolling for a short moment, auto-align to nearest section
  let scrollIdleTimer = null;
  function handleScrollIdle() {
    if (scrollAnimating) return; // skip if we're animating
    const currentY = root.scrollTop;
    // Find nearest section based on offsetTop distance
    let nearestIdx = 0;
    let nearestDist = Infinity;
    sections.forEach((sec, i) => {
      const dist = Math.abs(sec.offsetTop - currentY);
      if (dist < nearestDist) { nearestDist = dist; nearestIdx = i; }
    });
    // Only snap if we're not already essentially there and distance is reasonable
    if (nearestDist > 4) {
      animateScrollTo(sections[nearestIdx].offsetTop);
      setActive(nearestIdx);
    }
  }
  root.addEventListener('scroll', () => {
    if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
    // Debounce idle snap (quick 120ms after user lifts finger)
    scrollIdleTimer = setTimeout(handleScrollIdle, 120);
  }, { passive: true });

  // Provide an API hook for future dynamic tuning if needed
  window.__docuSnapConfig = {
    setThreshold(newT) {
      observer.disconnect();
      sections.forEach(s => observer.observe(s));
    },
    setDuration(ms) { /* runtime tweak */ },
  };

  // Wire dots
  dots.forEach((d, i) => {
    d.addEventListener('click', (e) => {
      e.preventDefault();
      const y = sections[i].offsetTop; // offsetTop relative to snap root
      animateScrollTo(y);
      sections[i].querySelector('h1,h2')?.focus();
    });
    d.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); d.click();
      }
    });
  });

  // Prev/next
  const prevBtn = root.querySelector('.snap-prev');
  const nextBtn = root.querySelector('.snap-next');
  if (prevBtn) prevBtn.addEventListener('click', () => {
    const idx = sections.findIndex(s => s.classList.contains('is-active'));
    if (idx > 0) animateScrollTo(sections[idx - 1].offsetTop);
  });
  if (nextBtn) nextBtn.addEventListener('click', () => {
    const idx = sections.findIndex(s => s.classList.contains('is-active'));
    if (idx < sections.length - 1) animateScrollTo(sections[idx + 1].offsetTop);
  });

  // Prevent inner scroll traps: overscroll-behavior containment
  root.style.overscrollBehavior = 'contain';

  // Disable native smooth behavior since we handle it manually
  root.style.scrollBehavior = 'auto';

  // Wheel support (desktop simulation): ensure mouse wheel scroll always affects snap root
  // This helps when testing in devtools responsive mode where body receives wheel events.
  root.addEventListener('wheel', (e) => {
    // If not active due to gating, skip; otherwise translate wheel delta directly.
    // Passive must be false so we can prevent default when needed.
    const delta = e.deltaY;
    if (Math.abs(delta) > 0) {
      // Apply a slight easing multiplier so small deltas still move enough to cross thresholds.
      root.scrollTop += delta * 0.9;
      // Prevent body/page from also scrolling.
      e.preventDefault();
    }
  }, { passive: false });

  return { observer };
}
